/**
 * Hook to get the current services provider based on URL route params
 * Uses TanStack Query to fetch and cache the services provider data
 *
 * The current services provider is determined by the servicesProviderId in the route.
 * This hook provides the single source of truth for the active services provider.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ServicesProvider } from '@ekhodealer/ekho-common';

import { getServicesProvider } from '@common/api/services-providers/useGetServicesProvider';
import { queries } from '@config/server/queryKeys';

import { useCurrentServicesProviderId } from './useCurrentServicesProviderId';

/**
 * Returns a query for the current active services provider based on route params
 * The query will be disabled if there's no services provider ID in the route
 */
export function useCurrentServicesProvider(): UseQueryResult<ServicesProvider, Error> {
	const currentServicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.servicesProviders.detail(currentServicesProviderId ?? '').queryKey,
		queryFn: () => {
			if (!currentServicesProviderId) {
				throw new Error('No services provider ID available');
			}
			return getServicesProvider(currentServicesProviderId);
		},
		enabled: !!currentServicesProviderId,
		staleTime: Infinity, // never stale unless the data is mutated
	});
}
